/**
 * The main logic engine class that is used to handle math, calculations, and
 * replacing values in text strings.
 */

// See Numeral.js - http://numeraljs.com/
import numeral from 'numeral';

// See MathJs for math engine - https://mathjs.org/docs/index.html
import * as mathjs from 'mathjs';

// Simple event emmitter - https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';

// Moment JS For Date Processing - https://momentjs.com/
import { DurationInputArg2, isDate } from 'moment';

import { DataQuery } from '../utils/DataQuery';
import { DataDictionary } from '../utils/DataDictionary';
import { StringOptions } from '../utils/StringOptions';
import { DataTools } from '../utils/DataTools';
import { TimeAgo } from '../utils/TimeAgo';
import { DataServices } from '../utils/DataServices';
import { DataFormatter } from '../dataformats/DataFormatter';
import { StepConditional } from '../PageParser/StepConditional';
import { ConditionEngine } from './ConditionEngine';

const { version } = require('../../package.json');

const globalDebugAssignLogic = false;

console.log('LogicEngine PlatFormshared Version:', version);

const globalSetValues: { [key: string]: any } = {};

function generateSessionID()
{
	let id = '';
	id = id + new Date().getTime().toString(22);
	id = id + ((Math.ceil(Math.random() * 1000000 + 1000) % 899) + 100).toString(22);
	id = id + ((Math.ceil(Math.random() * 1000000 + 1000) % 899) + 100).toString(22);
	id = id + ((Math.ceil(Math.random() * 1000000 + 1000) % 899) + 100).toString(22);
	return id;
}

export class LogicEngine
{
	static debug = false;
	math: Partial<mathjs.MathJsStatic>;
	parser: mathjs.Parser;

	/* Local copy of the data dictionary */
	_dataDictionary: DataDictionary | undefined;

	/* Cached version of values assigned or imported */
	values: { [keyName: string]: any } = {};

	static currentUser: string = 'system';
	static currentDeviceId: string = 'unknownDevice';
	static currentIpAddress: string = '127.0.0.1';
	static currentGroups: string[] = [];
	static currentLogicVersion: string = 'v' + version;
	static debugOutput: boolean = false;
	static debugWebdriver: boolean = false;
	static currentSessionID: string = generateSessionID();

	// TODO:  This method doesn't account for "header" or redirect or include files.
	static currentPageShowing: string = '';
	static currentLineNumber: number = 0;
	static currentCommandText: string = ''; // For debug output
	static currentPageFull: string = '';
	static currentSiteId: string = '';

	//  When you authorize as a participant, these get setup
	static currentIsParticipant: boolean = false;
	static currentParticipantID: string = '';

	//  Timezone offset to use for date math
	static timezoneOffset: number = 0;

	conditions: ConditionEngine;

	/** Events that are fired from a conditional change */
	evFieldChanged: SubEvent<StepConditional.FieldEnabledEvent> = new SubEvent();

	/** Debugging Hoook - See Demo/SpeedTest for usage */
	static debugLogicSet: (varname: string, value: any) => void = (varname, value) => { };

	/** When a value is assigned, loop through all current conditions and see if anything needs updating */
	runConditions(): StepConditional.FieldEnabledEvent[]
	{
		let resultList: StepConditional.FieldEnabledEvent[] = [];

		for (let c of this.conditions.conditions)
		{
			const conditional = this.processTextReplacement(c.conditionalText);

			if (LogicEngine.debugOutput)
				console.log(
					'LogicEngine Condition Check [' + c.conditionalText + '] = ' + conditional,
				);

			const result = c.checkCondition(conditional);
			if (result !== undefined)
			{
				if (result.eventType === 'FieldEnabled')
				{
					this.evFieldChanged.emit(result as StepConditional.FieldEnabledEvent);
					resultList.push(result as StepConditional.FieldEnabledEvent);
				} else throw 'Unknown conditional event type ' + result.eventType;
			}
		}

		return resultList;
	}

	/* Enable web driver on the current logic engine */
	enableWebdriver(isWebDriver: boolean)
	{
		LogicEngine.debugWebdriver = isWebDriver;
		if (isWebDriver)
		{
			for (const key of Object.keys(this.values))
			{
				console.log("ASSIGN:", DataTools.toJSON({ i: 1, var: key, val: this.values[key] }));
			}
		}
	}

	/**
	 * Import some value or function.  See mathjs import function.
	 *
	 * @param something An object to import or a compatible function
	 *
	 * @example
	 * import({ age : 50 });
	 *
	 */
	import(something: mathjs.ImportObject)
	{
		if (globalDebugAssignLogic) console.log('LogicEngine Import:', something);

		try
		{
			Object.assign(this.values, something);


			let importObject: any = {};
			for (let key of Object.keys(something))
			{
				let value = something[key];
				if (value === undefined || value === null)
				{
					importObject[key] = '';
					continue;
				}

				if (typeof value === 'object' && value.getTime)
				{
					value = (value as Date).toISOString();
				}

				if (LogicEngine.debugWebdriver)
					console.log("ASSIGN:", DataTools.toJSON({ var: key, val: value }));

				importObject[key] = value;
				LogicEngine.debugLogicSet(key, value);
			}

			if (LogicEngine.debugOutput) console.log('LogicEngine Import:', importObject);

			if (this.math)
			{
				(this.math as mathjs.MathJsStatic).import(importObject, {
					override: true,
					silent: false,
					wrap: false,
				});
			}

		} catch (e: any)
		{
			console.warn('Exception during LogicEngine.import trying to import object:', e.message);
			console.log('LogicEngine Import Stack:', e.Stack);
		}
	}

	/**
	 *  Assign a variable name to a value.  Similar to import but for a single item
	 *
	 * @param variableName  A key name for value
	 * @param value         A value to assign
	 *
	 * @example
	 * assign("example", { key: "example" })
	 */
	assign(variableName: string, value: any)
	{
		LogicEngine.debugLogicSet(variableName, value);

		if (variableName === "site_id" && value && typeof (value) === "string" && value.length > 0)
			LogicEngine.currentSiteId = value;

		if (variableName === "SiteDetail_id" && value && typeof (value) === "string" && value.length > 0)
			LogicEngine.currentSiteId = value;

		let obj: any = {};
		obj[variableName] = value;
		this.values[variableName] = value;
		this.import(obj);
		this.runConditions();
	}

	/**
	 * Execute a query on the server, save the total results to a value
	 *
	 * @param textQuery         The query to run
	 * @param textVariableName  The variable to save the total records in
	 *
	 * @example
	 * cmdCountQuery("Participant;status=C", "participant")
	 */
	async cmdCountQuery(textQuery: string, textVariableName: string): Promise<void>
	{
		const resultObject: any = {};
		resultObject[textVariableName] = 0;

		if (LogicEngine.debugOutput)
			console.log('LogicEngine cmdCountQuery [', textQuery, '] for variable:', textVariableName);

		let results = await DataQuery.instance().executeCountQueryByText(textQuery);
		resultObject[textVariableName] = results;

		this.import(resultObject);
	}

	/**  Execute a database query with the currently logged in credentials.
	@param textQuery    The query to execute
	@param textVariableNames [optional] Comma seperated list of variables from the return set to import.
	if not specified, the entire result set is imported.
	@param textPrefix [optional] An optional string to use before variable names imported.

	@example
	execute(cat("TaskData;task_id=", task_id), "task_status, task_title");
	*/
	async fnExecuteQuery(
		textQuery: string,
		textVariableNames?: string,
		textPrefix?: string,
	): Promise<boolean>
	{
		try
		{
			const resultObject: any = {};
			textVariableNames = DataTools.internalValidateString(textVariableNames);

			let results = await DataQuery.instance().executeQueryByText(textQuery);
			if (!results)
			{
				if (LogicEngine.debugOutput)
					console.log('LogicEngine executeQuery [', textQuery, '] no resulst');
			}

			for (const row of results)
			{
				for (let key of Object.keys(row.data))
				{
					if (key === '_id') continue;
					if (key === '__v') continue;
					if (textVariableNames === '' || textVariableNames.split(',').includes(key))
					{
						if (textPrefix && textPrefix.length > 0)
							resultObject[textPrefix + key] = row.data[key];
						else resultObject[key] = row.data[key];
					}
				}
			}

			if (LogicEngine.debugOutput)
				console.log('LogicEngine executeQuery [', textQuery, '] Import:', resultObject);

			this.import(resultObject);
		} catch {
			console.log('LogicEngine Exception in executeQuery:', textQuery);
			return false;
		}

		return true;
	}

	/**
	 * Find the value of a given variable or expression that has been set
	 *
	 * @param varName A value name to get from process or values from the logic engine
	 *
	 * @example
	 * getVariable("example")
	 *
	 */
	getVariable(varName: string): any
	{
		try
		{
			let value = this.process(varName);
			if (value != null && value != undefined)
			{
				return value;
			}
		} catch (e: any)
		{
			console.log('LogicEngine Process execption:', varName, '=', e);
		}

		if (this.values[varName]) return this.values[varName];

		if (this.values[varName.toLowerCase()]) return this.values[varName.toLowerCase()];

		return '';
	}

	/**
	 * Uses the values from the logic engine to process text within the engine
	 * looks for values like {{<expression>}} and replaces them.
	 *
	 * @param text The text to process
	 *
	 * @example
	 * processTextReplacement("Count is {{count}} and twice count is {{ 2 * count }}.")
	 */
	processTextReplacement(text: string | undefined): string
	{
		if (!text) return '';

		// Convert to a string if needed
		if (typeof text != 'string') text = DataTools.internalValidateString(text);

		const reMatch = new RegExp('{{([^{]+)}}', 'g');

		// Matchall is not available until node v12 so do a hack
		// to loop through all the options.
		const matches = text.match(reMatch);
		if (!matches) return text;

		for (let key of matches)
		{
			let commandExpression = key.replace('{{', '').replace('}}', '');
			// Need this because excel and some editors do weird stuff like
			// adding the non breaking space char 160 instead of space 32
			commandExpression = commandExpression.replace(/\s/g, ' ');
			let result = this.process(commandExpression);
			text = text.replace(key, result);
		}

		if (text && text.length > 0) return text.trim();
		return text;
	}

	/**
	 * Execute a set command.  See "SetValue" readme
	 *
	 * Type / Command / Option 1 / Option 2
	 * CountQuery / QueryToRun / Variable to store (default to count) / <not used>
	 *
	 * @param textType The type of command to run
	 * @param textCommand The command details
	 * @param variableName Optional information 1
	 * @param textOption2 Optional information 2
	 *
	 * @example
	 * processSetCommand("CountQuery", "Participant;status=R", "count");
	 */
	async processSetCommand(
		textType: string,
		textCommand: string,
		variableName?: string,
		textOption2?: string,
	)
	{
		// console.log("LogicEngine set [type=", textType, "] cmd=[", textCommand, "] variableName=[", variableName, "][", textOption2, "]");

		const type: StringOptions = new StringOptions(textType);
		if (type.checkOption('countquery'))
		{
			if (!variableName) variableName = 'count';
			await this.cmdCountQuery(textCommand, variableName);
		} else
		{
			let field = this.processTextReplacement(textType);
			let command = this.processTextReplacement(textCommand);

			// Set global values with a name like #global or #age where the # means global
			// does not reset on each page load.
			if (field.startsWith('#'))
			{
				field = field.replace('#', '');
				globalSetValues[field] = command;
			}

			// Text replacement in the variable name
			field = this.processTextReplacement(field);

			// Remove spaces from the variable name
			field = field.replace(/ /g, '');

			this.assign(field, command);

			if (LogicEngine.debugOutput) console.log('LogicEngine Set', field, '=', command);

			// TODO Better error handler for failed command reporting
			// console.log("LogicEngine::processSetCommand unknowng command type=", textType);
			// return null;
		}
	}

	/**
	 * Evaluate an expression and return the result of that expression.
	 *
	 * @param strExpress the expression to evaluate
	 *
	 * @example
	 * process("current_route")
	 */
	process(strExpress: string, attemptCount: number = 0): any
	{
		if (attemptCount > 10)
		{
			throw 'Unable to process:' + strExpress + ' aftern 10 attemps.';
			return null;
		}

		if (LogicEngine.debug) console.log('LogicEngine Process: ', strExpress);

		// Check for logical and
		const matches = /(.*) and (.*)/i.exec(strExpress);
		if (matches && matches.length > 0)
		{
			const item1 = matches[1];
			const item2 = matches[2];

			if (LogicEngine.debugOutput)
				console.log("LogicEngine 'AND' Checking [", item1, '] and [', item2, ']');

			let result1 = this.process(item1, attemptCount + 1);
			let result2 = this.process(item2, attemptCount + 1);

			if (LogicEngine.debugOutput)
			{
				console.log("LogicEngine AND Condition Left = {{", item1, "}} = ", result1);
				console.log("LogicEngine AND Condition Right = {{", item2, "}} = ", result2);
				console.log('LogicEngine logical and ', result1, ' and ', result2, '=', result1 && result2);
			}

			return result1 && result2;
		}

		const matchesor = /(.*) or (.*)/i.exec(strExpress);
		if (matchesor && matchesor.length > 0)
		{
			const item1 = matchesor[1];
			const item2 = matchesor[2];

			if (LogicEngine.debugOutput)
				console.log("LogicEngine 'OR' Checking [", item1, '] or [', item2, ']');

			let result1 = this.process(item1, attemptCount + 1);
			let result2 = this.process(item2, attemptCount + 1);

			if (LogicEngine.debugOutput)
			{
				console.log("LogicEngine OR Condition Left = {{", item1, "}} = ", result1);
				console.log("LogicEngine OR Condition Right = {{", item2, "}} = ", result2);
				console.log('LogicEngine logical or ', result1, ' or ', result2, '=', result1 && result2,);
			}

			return result1 || result2;
		}

		try
		{
			const result = this.parser.evaluate(strExpress);

			// if (LogicEngine.debugOutput)
			// console.log('LogicEngine Result : ', result);

			return result;
		} catch (e: any)
		{
			//  Do not warn or make an error for a given set of common lookups
			if (strExpress === 'participant_uuid') return null;
			if (strExpress === 'participant_id') return null;
			if (strExpress === 'task_id') return null;

			// Search for the specific undefined symbol which means we're trying to access
			// an object that has not been defined.
			if (e.message)
			{
				const reSearchError = new RegExp('Undefined symbol ([a-zA-Z0-9_.]+)');
				const searchMatch = reSearchError.exec(e.message);
				if (searchMatch && searchMatch.length > 1)
				{
					// Found the pattern, the name of the variable is in the 1 index of the result.
					// define this and try the process again.

					if (LogicEngine.debugOutput)
						console.log(
							"LogicEngine process, missing variable error '",
							searchMatch[1],
							', attemping again:',
							attemptCount + 1,
						);

					this.assign(searchMatch[1], '');

					return this.process(strExpress, attemptCount + 1);
				}
			}

			// DataTools.reportException('LogicEngine Parser for ' + strExpress, e);
			console.error(
				"LogicEngine Parser error for '",
				strExpress,
				"' in row ",
				LogicEngine.currentCommandText,
				':',
				LogicEngine.currentLineNumber,
				' => ',
				e,
			);

			// TODO: Better exception handling.  What should happen and how can we
			// detect an invalid function call?
		}

		return null;
	}

	async initialize(): Promise<void>
	{
		this._dataDictionary = await DataServices.instance().getDataDictionary();
		this.assign('session_id', LogicEngine.currentSessionID);
		this.assign('current_page', LogicEngine.currentPageShowing);
	}

	/**
	 * Math function that returns a number and either the singular or plural form of a word.
	 *
	 * @param value     A number of quantity
	 * @param singular  A word for singular form
	 * @param plural    A word for plural form
	 *
	 * @example {{ suffix(num, 'baby', 'babies') }}
	 */
	fnSuffix(value: number, singular: string, plural: string)
	{
		// check if number is negative
		if (value < 0) value = -value;
		if (value === 1) return value + ' ' + singular;

		return numeral(value).format('#,###') + ' ' + plural;
	}

	/**
	 * Logic command that returns the current date and time
	 *
	 * @category Logic Command.
	 *
	 * @returns Current time date as an ISO string
	 *
	 * @example {{ now() }}
	 */
	fnNow(timezone_offset_seconds: number)
	{
		if (timezone_offset_seconds === undefined) timezone_offset_seconds = new Date().getTimezoneOffset();
		let now = DataTools.getCurrentDateWithOffset(timezone_offset_seconds);
		return now.toISOString();
	}

	/**
	 * Logic command that returns the current year
	 * @category Logic Command.
	 * @returns Current year as 4 digits
	 * @example {{ current_year() }}
	 */
	fnCurrentYear()
	{
		return new Date().getFullYear();
	}

	/** Get the current hour with the application of the timeonze
	see DataTools for timezone offfset */
	fnCurrentHour(timezone_offset_seconds: number | undefined)
	{
		if (timezone_offset_seconds === undefined) timezone_offset_seconds = new Date().getTimezoneOffset();
		let now = DataTools.getCurrentDateWithOffset(timezone_offset_seconds);
		return now.getHours();
	}

	/**
	 * Logic command that returns the current user
	 * @category Logic Command.
	 * @returns Current user
	 * @example {{ current_user() }}
	 */
	fnCurrentUser()
	{
		return LogicEngine.currentUser;
	}

	fnCurrentParticipant()
	{
		return LogicEngine.currentParticipantID;
	}

	/**
	 * Displays the text value from an option
	 * @category        Logic Command
	 * @param fieldName A value name to convert based on a lookup table
	 * @param value     A value to convert based on a lookup table
	 * @example {{ lookup("Field Name", value) }}
	 */
	fnLookup(fieldName: string, value: string): string
	{
		if (this._dataDictionary)
		{
			let result = this._dataDictionary.getLookupAsText(fieldName, value);
			if (result && typeof result === 'string') result = result.trim();
			return result;
		}

		return 'MISSING DD(' + value + ')';
	}

	/**
	 * Logic command Convert a value to a display date
	 * @category    Logic Command
	 * @param value A human readable date string
	 * @example {{ datedisplay("2021-02-19") }}
	 */
	fnDisplayDate(value: string, format: string)
	{
		if (format === undefined || format === null || format === '') format = 'l';

		if (value == undefined || value === null)
		{
			if (LogicEngine.debugOutput)
				console.log(
					'LogicEngine fnDisplayDate ',
					value,
					' (format=' + format + ') = not set',
				);
			return 'Not set';
		}

		const m = DataTools.getMoment(value);
		if (m)
		{
			let result = m.format(format);
			if (LogicEngine.debugOutput)
				console.log(
					'LogicEngine fnDisplayDate ',
					value,
					' (format=' + format + ') = ',
					result,
				);
			return result;
		}

		if (LogicEngine.debugOutput)
			console.log(
				'LogicEngine fnDisplayDate ',
				value,
				' (format=' + format + ') = unkown or invalid',
			);

		return '[' + value + ']';
	}

	/**
	 * logic command ms_to_years converts a value in milliseconds
	 * to a number of years.  Used with ellapsed time you can determine
	 * the age of something.
	 * @category Logic Command
	 * @param value_ms The number of milliseconds
	 * @return a number of years without a fraction
	 * @example {{ ms_to_years(12341) }}
	 */
	fnMsToYears(value_ms: number)
	{
		const sec = value_ms / 1000;
		const min = sec / 60;
		const hrs = min / 60;
		const days = hrs / 24;
		const years = days / 365.25;
		return Math.trunc(years);
	}

	/**
	 * logic command ms_to_years converts a value in milliseconds
	 * to a number of years.  Used with ellapsed time you can determine
	 * the age of something.
	 * @category Logic Command
	 * @param value_ms The number of milliseconds
	 * @return a number of years without a fraction
	 * @example {{ ms_to_days(243223) }}
	 */
	fnMsToDays(value_ms: number)
	{
		const sec = value_ms / 1000;
		const min = sec / 60;
		const hrs = min / 60;
		let days = hrs / 24;

		let message = '';
		if (days > 1)
		{
			message = Math.trunc(days).toString() + ' days';
			days -= Math.trunc(days);
		} else if (days > 0)
		{
			message = Math.trunc(days).toString() + ' day';
			days -= Math.trunc(days);
		}

		const h = Math.trunc(24 * days);
		if (h > 0)
		{
			if (message !== '') message += ', ';
			if (h > 1)
			{
				message += h.toString() + ' hrs.';
			}
		} else if (h < 0)
		{
			message += h.toString() + ' hrs.';
		}

		return message;
	}

	/**
	 * Logic command ellapsed_time takes 2 time/date values and
	 * converts them into number based on the total number of
	 * milliseconds that has passed from "A" to "B"
	 * @category Logic Command
	 * @param timeA The larger time value (most recent)
	 * @param timeB The smaller time value (oldest)
	 * @returns The number of ms between times.
	 * @example {{ ellapsed_time("2021-02-19", "2021-02-01") }}
	 */
	fnEllapseTime(timeA: string, timeB: string)
	{
		if (LogicEngine.debugOutput) console.log('LogicEngine fnEllapsedTime', timeA, '-', timeB);
		const a = DataTools.getNumber(timeA);
		const b = DataTools.getNumber(timeB);
		if (!a) return 0;
		if (!b) return 0;
		// const a: Date = new Date(timeA);
		// const b: Date = new Date(timeB);
		// const ellapsedMS = (a as any) - (b as any);
		const ellapsedMS = a - b;
		return ellapsedMS;
	}

	/**
	 * Logic command ellapsed_time takes 2 time/date values and
	 * converts them into number based on the total number of
	 * milliseconds that has passed from "A" to "B"
	 * @category Logic Command
	 * @param timeA The larger time value (most recent)
	 * @param timeB The smaller time value (oldest)
	 * @returns The number of ms between times.
	 * @example {{ ellapsed_time("2021-02-19", "2021-02-01") }}
	 */
	fnDaysAgo(timeA: string, timezone_offset_seconds: number)
	{
		if (timezone_offset_seconds === undefined) timezone_offset_seconds = new Date().getTimezoneOffset();
		let now = DataTools.getCurrentDateWithOffset(timezone_offset_seconds);

		let m = DataTools.getMoment(timeA);
		if (!m)
		{
			if (LogicEngine.debugOutput) console.log('LogicEngine fnDaysAgo invalid time:', timeA);
			return 0;
		}

		const ellapsedMS = now.getTime() - m.toDate().getTime();
		const ellapsedDays = ellapsedMS / 86400000;
		return ellapsedDays;
	}

	/** Returns true if the date is in the future */
	fnIsFuture(timeA: string, timezone_offset_seconds: number)
	{
		const days = this.fnDaysAgo(timeA, timezone_offset_seconds);
		return days < 0;
	}

	/** Logic command that adds something to a date
	See https://momentjs.com/guides/ for instructions on date math
	*/
	fnDateMath(timeA: string, amount: number, type: string)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnDateMath Source:', timeA, ' Adding ', amount, ' of ', type);
		const a = DataTools.getMoment(timeA);
		if (!a)
		{
			if (LogicEngine.debugOutput) console.log('LogicEngine fnDateMath unable to get Moment');
			return 0;
		}

		let newTime = a.add(amount, type as DurationInputArg2);
		return newTime.toDate().toISOString();
	}

	/**
	 * Logic command that will check to see if the user is part of one
	 * or more groups as specified by a comma list.
	 * @param groupNameList List of possible groups.
	 * @returns true if the user is in one of those groups
	 * @example {{ has_group("AdminUsers,AdminWrite")}}
	 */
	fnHasGroup(groupNameList: string)
	{
		const groupSearch = DataTools.internalValidateString(groupNameList).split(',');
		for (let searchName of groupSearch)
		{
			searchName = searchName.toLocaleLowerCase();
			for (let realGroup of LogicEngine.currentGroups)
			{
				realGroup = realGroup.toLocaleLowerCase();
				if (realGroup.trim() == searchName.trim()) return true;
			}
		}

		return false;
	}

	/**
	 * Returns a list of the groups this user has permission to use
	 * @example get_groups()
	 */
	fnGetGroups()
	{
		if (!LogicEngine.currentGroups) return '';

		if (LogicEngine.currentGroups.length === 0) return '';

		return LogicEngine.currentGroups.join(',');
	}

	/**
	 * Concat multiple elements together into a string
	 * @category Logic Command
	 * @param args Any number of elements to cat
	 * @see https://support.microsoft.com/en-us/office/concatenate-function-8f8ae884-2ca8-4f7a-b093-75d702bea31d
	 * @example {{ concatenate("example1", "example2") }}
	 */
	fnConcatenate(...args: any)
	{
		let result = '';
		for (let i of args)
		{
			if (typeof i === 'string')
			{
				result += i;
			} else if (i.toString)
			{
				result += i.toString();
			} else
			{
				result += '[unknown ' + typeof i + ']';
			}
		}

		return result;
	}

	/**
	 * Returns a substring from the start of the text
	 * @param sourceValue Source String
	 * @param lenStart number of characters
	 */
	fnSubstring(sourceValue: string, lenStart: number)
	{
		if (typeof sourceValue !== 'string') sourceValue = JSON.stringify(sourceValue);

		if (typeof lenStart != 'number')
		{
			const num = parseInt(lenStart, 10);
			lenStart = num;
		}

		let result = sourceValue.substr(0, lenStart);
		return result;
	}

	/**
	 * Logic Command "replace" - Returns a string with a substring reaplced
	 * @category Logic Command
	 * @param sourceValue Source String
	 * @param sourceSearch Search text to find
	 * @param replaceText Replacement Text
	 * @example {{ replace("example", "ex", "ab") }}
	 */
	fnReplace(sourceValue: string, sourceSearch: string, replaceText: string)
	{
		let result = sourceValue.replace(sourceSearch, replaceText);
		return result.trim();
	}

	/**
	 * Logic Command "replace" - Returns a string with a substring reaplced
	 * @category Logic Command
	 * @param sourceValue Source String
	 * @param sourceSearch Search text to find
	 * @param replaceText Replacement Text
	 * @example {{ replace("example", "ex", "ab") }}
	 */
	fnReplaceAll(sourceValue: string, sourceSearch: string, replaceText: string)
	{
		let re = new RegExp(sourceSearch, 'gi');
		let result = sourceValue.replace(re, replaceText);
		return result.trim();
	}

	/** Encode text using the safe magic encoder in data tools
	@category Logic Command
	@param sourceValue Any value that will be encoded as a string
	*/
	fnEncode(sourceValue: string)
	{
		return DataTools.encodeObject(DataTools.internalValidateString(sourceValue));
	}

	/** Encode text using the safe magic encoder in data tools
	@category Logic Command
	@param sourceValue Any value that will be encoded as a string
	*/
	fnDecode(sourceValue: string)
	{
		return DataTools.decodeObject(DataTools.internalValidateString(sourceValue));
	}


	/**
	 * Logic function equal(a, b) that compares two values as
	 * strings to see if they are the same.  Converts to lowercase.
	 * @category Logic Commands
	 * @param sourceValue A source value to compare
	 * @param targetValue A target value to compare
	 * @example {{ equal("example", "example") }}
	 */
	fnEqual(sourceValue: any, ...targetValues: any)
	{
		for (let targetValue of targetValues)
		{
			// check if both are same type
			if (typeof sourceValue === typeof targetValue)
			{
				// check if both are string
				if (typeof sourceValue === 'string')
				{
					let a = sourceValue.toString().trim().toLocaleLowerCase();
					let b = targetValue.toString().trim().toLocaleLowerCase();
					{
						if (a === b) return true;
						continue;
					}
				}

				if (sourceValue === targetValue) return true;
				continue;
			}

			if (typeof sourceValue === 'string' && sourceValue.toLowerCase() == "yes" && targetValue === true)
				return true;

			if (typeof sourceValue === 'string' && sourceValue.toLowerCase() == "no" && targetValue === false)
				return true;

			if (typeof sourceValue === 'boolean' && typeof targetValue === 'string' && sourceValue === true && targetValue.toLowerCase() === "yes")
				return true;

			if (typeof sourceValue === 'boolean' && typeof targetValue === 'string' && sourceValue === false && targetValue.toLowerCase() === "no")
				return true;

			// if both are null or undefined, then true
			if (!sourceValue && !targetValue) continue;
			// otherwise get Number value from getOperators
			let [sourceNumber, targetNumber] = DataTools.getOperators(sourceValue, targetValue);
			// check if the operation is valid
			if (sourceNumber === null || targetNumber === null) continue;
			// check if sourceValue and targetValue are same
			if (sourceNumber === targetNumber) return true;
		}

		return false;
	}

	/** Logic function to search for a needle within a haystack.  Case is ignored.  Spacing is ignored.
	@example
	{{ contains("ap", "apple") }}
	{{ contains("ball", "WaterBall Team")
	*/
	fnContains(needle: string, haystack: string)
	{
		let n = DataTools.internalValidateString(needle).replace(/ /g, '').toLocaleLowerCase();
		let h = DataTools.internalValidateString(haystack).replace(/ /g, '').toLocaleLowerCase();

		if (LogicEngine.debugOutput) console.log('fnContains needle=', n, 'haystack=', h);

		if (h.indexOf(n) != -1) return true;
		return false;
	}

	/**
	 * Logic Function to match the pattern within the search string
	 * @param pattern The regex pattern to match without / /
	 * @param haystack The full text to search
	 * @param output1 The name of the variable for group 1
	 * @param output2 the name of the variable for group 2
	 * @param output3 the name of the variable for group 3
	 * @returns
	 */
	fnMatch(pattern: string, haystack: string, output1: string, output2: string, output3: string)
	{
		let strPattern = DataTools.internalValidateString(pattern);
		let strHaystack = DataTools.internalValidateString(haystack);

		if (LogicEngine.debugOutput)
			console.log('fnMatch pattern=', strPattern, ' haystack=', strHaystack);

		let regExp = new RegExp(strPattern, 'i');
		const output = regExp.exec(strHaystack);
		if (!output) return false;

		if (output1) this.assign(output1, output[1]);

		if (output2) this.assign(output2, output[2]);

		if (output3) this.assign(output3, output[3]);

		if (output.groups)
		{
			console.log('GROUPS=', output.groups);
		}

		return true;
	}

	/** Logic Function that returns the length of the string. If you send in something else such as a date or
	number, it's converted it a string before the length is taken. The value is trimmed of sapces first. */
	fnLength(something: string)
	{
		let value = DataTools.internalValidateString(something).trim();
		let len = value.length;
		return len;
	}

	/**
	 * Logic function to check value in lookup value as it is expected
	 * @category Logic Commands
	 * @param sourceValue A value to lookup
	 * @param table A lookup table
	 * @param targetValue An expected value
	 * @example {{ lequal(is_baby, “OptionalBoolean”, “Yes”) }}
	 */
	fnLookupEqual(sourceValue: any, table: string, targetValue: any): boolean
	{
		const lookupValue = this.fnLookup(table, sourceValue);
		return this.fnEqual(targetValue, lookupValue);
	}

	/**
	 * Logic function route_match(value) that check if the vlaue matches current route
	 * @category Logic Commands
	 * @param searchValue A string value to check if it matches to the current route
	 * @example {{ route_match("example/route") }}
	 */
	fnRouteMatch(searchValue: string)
	{
		const current = this.process('current_route');
		if (current)
		{
			const reExtended = new RegExp(searchValue + '_', 'i');
			if (reExtended.test(current.toString()))
			{
				return false;
			}

			const re = new RegExp(searchValue, 'i');
			if (re.test(current.toString()))
			{
				return true;
			}
		}

		return false;
	}

	fnJson(someValue: string, someField: string)
	{
		try
		{
			const obj = DataTools.fromJSON(DataTools.internalValidateString(someValue));
			const result = (obj as any)[someField];
			if (LogicEngine.debugOutput)
				console.log("LogicEngine fnJson Parse ", someValue, "field=", someField, " return", result);
			return result;
		} catch
		{
			if (LogicEngine.debugOutput)
				console.log("LogicEngine fnJson failed source=", someValue, " field=", someField);
			return "";
		}
	}

	/** Logic "not" some value.  Excepts and text or boolean that can be
	 * understood by the boolean formatter such as Yes, true, 1, etc.
	 * @category Logic Commands
	 * @param someValue A value that can be understood by boolean formatter
	 * @returns true or false
	 * @example {{ not('Yes') }}
	 */
	fnNot(someValue: any)
	{
		if (someValue === undefined) return true;
		if (someValue === null) return true;
		if (someValue === '') return true;

		let df = new DataFormatter();
		const boolValue = df.unformatData('boolean', someValue);
		return boolValue !== true;
	}

	/** Logical "Greater" function for numbers
	 * @category Logic Commands
	 *
	 * @param sourceValue A value to check for empty
	 * @param targetValue The value to use if the empty value is not empty
	 *
	 * @returns true or the value on the right
	 *
	 * @example {{ fnEmptyOr(variable, now()) }}
	 */
	fnEmptyOr(sourceValue: any, otherValue: any)
	{
		if (LogicEngine.debugOutput) console.log('LogicEngine fnEmptyOr Testing:', sourceValue);
		if (sourceValue === null) return true;
		if (sourceValue === undefined) return true;
		if (typeof sourceValue === 'string' && sourceValue.trim() === '') return true;
		if (otherValue === undefined) return false;
		return otherValue;
	}

	/** Logical "Greater" function for numbers
	 * @category Logic Commands
	 *
	 * @param sourceValue the left value is the value to check
	 * @param targetValue the right value is the value to compare against
	 *
	 * @returns true or false
	 *
	 * @example {{ greaterthan(10, 20) }}
	 */
	fnGreaterThan(sourceValue: any, targetValue: any)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnGreaterThan ', sourceValue, ' > ', targetValue);
		// get operators
		let [sourceNumber, targetNumber] = DataTools.getOperators(sourceValue, targetValue);
		// check if the operation is valid
		if (sourceNumber === null || targetNumber === null) return false;
		// compare values
		return sourceNumber > targetNumber;
	}

	/** Logical "Greater than or equal to" function for numbers
	 * @category Logic Commands
	 * @returns true or false
	 * @param sourceValue the left value is the value to check
	 * @param targetValue the right value is the value to compare against
	 * @example {{ greater_equal(10, 20) }}
	 */
	fnGreaterEqual(sourceValue: any, targetValue: any)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnGreaterEqual ', sourceValue, ' >= ', targetValue);
		// get operators
		let [sourceNumber, targetNumber] = DataTools.getOperators(sourceValue, targetValue);
		// check if the operation is valid
		if (sourceNumber === null || targetNumber === null) return false;
		// compare values
		return sourceNumber >= targetNumber;
	}

	/** Logical "Less thanr" function for numbers
	 * @category Logic Commands
	 * @param sourceValue the left value is the value to check
	 * @param targetValue the right value is the value to compare against
	 * @returns true or false
	 * @example {{ lessthan(10, 20) }}
	 */
	fnLessThan(sourceValue: any, targetValue: any)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnLessThan ', sourceValue, ' < ', targetValue);
		// get operators
		let [sourceNumber, targetNumber] = DataTools.getOperators(sourceValue, targetValue);
		// check if the operation is valid
		if (sourceNumber === null || targetNumber === null) return false;
		// compare values
		return sourceNumber < targetNumber;
	}

	/** Logical "Less than or equal to" function for numbers
	 * @category Logic Commands
	 * @param sourceValue the left value is the value to check
	 * @param targetValue the right value is the value to compare against
	 * @returns true or false
	 * @example {{ lessequal(10, 20) }}
	 */
	fnLessEqual(sourceValue: any, targetValue: any)
	{
		console.log('LogicEngine fnLessEqual ', sourceValue, ' <= ', targetValue);
		// get operators
		let [sourceNumber, targetNumber] = DataTools.getOperators(sourceValue, targetValue);
		// check if the operation is valid
		if (sourceNumber === null || targetNumber === null) return false;
		// compare values
		return sourceNumber <= targetNumber;
	}

	/** Logical "Less than" function for numbers
	 * @category Logic Commands
	 * @param sourceValue A value to check if it is in the range
	 * @param targetValue A small value of the range
	 * @param targetValueMax A max value of the range
	 * @returns true or false
	 * @example {{ in_rangex, 10, 20) }}
	 */
	fnInRange(sourceValue: any, targetValue: any, targetValueMax: any)
	{
		if (LogicEngine.debugOutput)
			console.log(
				'LogicEngine fnInRange ',
				targetValue,
				' <= ',
				sourceValue,
				' >= ',
				targetValueMax,
			);

		// compare as numbers
		let sourceNumber = DataTools.getNumber(sourceValue);
		let targetNumber = DataTools.getNumber(targetValue);
		let targetNumberMax = DataTools.getNumber(targetValueMax);
		// check if sourceNumber, targetNumber and targetNumberMax are valid number
		if (sourceNumber === null || targetNumber === null || targetNumberMax === null)
			return false;

		return sourceNumber >= targetNumber && sourceNumber <= targetNumberMax;
	}

	/** Logical function that return true if all values passed in are true
	 * @category Logic Commands
	 * @returns true or false
	 * @example {{ alltrue(true, "Yes", 10) }}
	 */
	fnAllTrue(...args: any[])
	{
		let df = new DataFormatter();

		for (let i of args)
		{
			const boolValue = df.unformatData('boolean', i);
			// console.log("LogicEngine fnAllTrue(",i,")=",boolValue);
			if (!boolValue) return false;
		}

		return true;
	}

	/** Logical function that return true if all values passed in are true
	 * @category Logic Commands
	 * @param numCountText A value for number of args
	 * @returns true or false
	 * @example {{ some_true(true, "Yes", 10) }}
	 */
	fnSomeTrue(numCountText: any, ...args: any[])
	{
		const numCount = DataTools.getNumber(numCountText);
		// check if numCount is null
		if (numCount === null) return false;

		let df = new DataFormatter();
		let trueCount: number = 0;

		for (let i of args)
		{
			const boolValue = df.unformatData('boolean', i);
			if (boolValue) trueCount++;
		}
		return trueCount >= numCount;
	}

	/** Logical function that return true if one value is true
	 * @category Logic Commands
	 * @returns true or false
	 * @example {{ onetrue(true, "Yes", 10) }}
	 */
	fnOneTrue(...args: any[])
	{
		let df = new DataFormatter();

		for (let i of args)
		{
			const boolValue = df.unformatData('boolean', i);
			if (boolValue) return true;
		}

		return false;
	}

	/** Logic function to conver a mongodb _id value into a date/time */
	fnDateFromObjectId(value: string)
	{
		const timestamp = parseInt(DataTools.internalValidateString(value).substr(0, 8), 16) * 1000;
		const result = new Date(timestamp);
		if (LogicEngine.debugOutput)
			console.log("LogicEngine.fnDateFromObject in=", value, "result ts=", timestamp);
		return result;
	}

	/** Logical function that prettifies date value
	 * @category Logic Commands
	 * @param value A date value to preittify
	 * @returns true or false
	 * @example {{ onetrue(true, "Yes", 10) }}
	 */
	fnPrettyDateTime(value: any)
	{
		let dateValue = DataTools.getMoment(value);
		if (!dateValue) return '[Invalid Date:' + value + ']';

		return dateValue.format('llll');
	}

	/**
	 * Logical function to remove html tages and reduce to a small summary text
	 * @category Logic Commands
	 * @param value A value to get a summary from
	 * @example {{ summary_text("example") }}
	 */
	fnSummaryText(value: any)
	{
		let text = DataTools.internalValidateString(value);
		text = text.replace(/<[^>]*>/g, '');
		if (text.length > 80)
		{
			text = text.substr(0, 80) + '...';
		}
		return text;
	}

	/** Logic Function that will add some number of days to a given date
	 * Converts the dayText to a date and adds full or partial days to it
	 * returns an Iso date string.
	 * @category Logic Commands
	 * @param dayText A date value
	 * @param numDaysText A value to add days to dayText
	 * @example {{ add_days("2021-02-19", "10") }}
	 */
	fnAddDays(dayText: any, numDaysText: any)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnAddDays(', dayText, numDaysText, ')');

		let startDate = DataTools.getMoment(dayText);
		let numDays = DataTools.getNumber(numDaysText);

		if (startDate)
		{
			startDate.add(numDays, 'days');
			return startDate.toDate().toISOString();
		} else
		{
			return numDays;
		}
	}

	/** Logic Function that takes a date/time and returns a human readable format
	 * that is a reference from the current time.  For example, 3 minutes ago.
	 * @category Logic Commands
	 * @param dateText A date value to compare current time
	 * @example {{ time_ago("2021-02-19") }}
	 */
	fnTimeAgo(dateText: any)
	{
		const m = DataTools.getMoment(dateText);
		if (!m) return '[' + dateText + ']';
		return TimeAgo.timeAgo(m.toDate());
	}

	fnCounterReset(name: string)
	{
		let varName = this.processTextReplacement(name);
	}

	/** Return true if the current logged in user is a participant account */
	fnIsParticipant()
	{
		if (LogicEngine.currentIsParticipant) return true;
		return false;
	}

	/** Logic Function that returns the timezone offset in seconds for the current user */
	fnTimezoneOffset()
	{
		let now = new Date();
		return now.getTimezoneOffset();
	}

	fnDateEquals(sourceValue: any, targetValue: any, days_offset: number, tz_offset: number)
	{
		if (days_offset === undefined) days_offset = 0;
		if (days_offset === null) days_offset = 0;
		if (typeof days_offset === 'string') days_offset = parseInt(days_offset, 10);

		if (tz_offset === undefined) tz_offset = 0;
		if (tz_offset === null) tz_offset = 0;
		if (typeof tz_offset === 'string') tz_offset = parseInt(tz_offset, 10);

		if (LogicEngine.debugOutput)
			console.log(
				`fnDateEquals '${sourceValue}' === '${targetValue}' with offset ${days_offset}`,
			);

		//  Convert the source to a date
		if (!sourceValue || sourceValue === '') return false;

		if (!targetValue || targetValue === '') return false;

		let m1 = DataTools.getMoment(sourceValue);
		let m2 = DataTools.getMoment(targetValue);

		if (!m1 || !m2)
		{
			if (LogicEngine.debugOutput)
				console.log('LogicEngine fnDateEquals: Unable to get a moment for source or target', m1, m2,);
			return false;
		}

		let d1 = m1.toDate();
		let d2 = m2.toDate();
		d1.setHours(0, 0, 0);
		d2.setHours(0, 0, 0);
		d1.setMilliseconds(0);
		d2.setMilliseconds(0);

		let gt1 = d1.getTime();
		let gt2 = d2.getTime();
		gt2 -= tz_offset * 1000;
		gt2 += days_offset * 86400000;

		// console.log("LogicEngine fnDateQuals comparing ", gt1, " to ", gt2);
		// console.log("LogicEngine fnDateQuals comparing ", m1, " to ", m2);
		// console.log("LogicEngine fnDateQuals comparing ", new Date(gt1), " to ", new Date(gt2));
		if (gt1 === gt2) return true;
		return false;
	}

	/** Return true if sourceValue is in the comma seperated list target value
	example in_list('D', 'A,B,C,D,E')
	*/
	fnInList(sourceValue: string, targetValue: string)
	{
		if (typeof sourceValue !== 'string')
		{
			if ((sourceValue as any).toString) sourceValue = (sourceValue as any).toString();
			else throw 'Expecting string sourceValue in fnInList.';
		}

		let list = targetValue.split(',');
		for (const item of list)
		{
			if (sourceValue.toLowerCase().trim() === item.toLowerCase().trim())
			{
				return true;
			}
		}

		return false;
	}

	/**
	 * Logic Function that takes condition and result values
	 * @category Logic Commands
	 * @param condition A condition to check
	 * @param result A value to return if condition is true
	 * @param elseResult A value to return if condition is false
	 * @returns A result value if condition is true, elseResult value if condition is false
	 * @example {{ add_high_priority(true, 10, -10) }}
	 */
	fnIf(condition: any, result: any, elseResult?: any): any
	{
		let df = new DataFormatter();
		// check if condition is true using getBoolean
		if (df.unformatData('boolean', condition)) return result;
		if (elseResult) return elseResult;
		return null;
	}

	/** Internal helper function that returns the combined variable name for index functions.
	For example people_john or just people depending on what is specified. */
	internalIndexVariableName(variableName: string, indexName?: string)
	{
		variableName = DataTools.internalValidateString(variableName);
		indexName = DataTools.internalValidateString(indexName);

		variableName = variableName.replace(/[^a-zA-Z0-9]/g, "");
		indexName = indexName.replace(/[^a-zA-Z0-9]/g, "");

		if (!/^[a-zA-Z]/.test(variableName))
			variableName = "v" + variableName;

		if (variableName === '' && indexName.length > 0) return indexName;

		if (variableName === '')
		{
			console.log('LogicEngine internalIndexVariableName Missing name of variable to reset');
			return '';
		}

		// Actual variable name in logic engine will be name_index such as
		// people_john or people_tom or just "people" if missing indexName
		let varName = variableName + (indexName.length > 0 ? '_' + indexName : '');
		varName = varName.replace(/^_/, "");
		return varName;
	}

	/**
	 * Index Reset command turns a variable to empty or to the value in NewValue if specified.
	 * @param variableName Name of the variable to reset
	 * @param indexName Name of the index within the variable to reset. Will be "" if not specified
	 * @param newValue optional value, will be 0 if not specified.
	 * @returns newValue
	 * @example index_rest("people", "john", 0);
	 * Set's either variable or variable_indexName
	 */
	fnIndexReset(variableName: string, indexName?: string, newValue?: any)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnReset variableName=', variableName, 'indexName=', indexName);

		const varName = this.internalIndexVariableName(variableName, indexName);
		if (!varName) return 0;

		// Default value is 0
		if (newValue === undefined || newValue === null) newValue = 0;

		this.assign(varName, newValue);
		return newValue;
	}

	/**
	 * Increment a value using the index function.  Amount is optional and defaults to 1
	 * If the value was not previously set, it's default value is 0 to start with.
	 * @param variableName Name of the variable.
	 * @param indexName Array index name
	 * @param amount Amount to increment
	 * @returns new value
	 */
	fnIndexIncrement(variableName: string, indexName?: string, amount?: number)
	{
		if (LogicEngine.debugOutput)
			console.log(
				'LogicEngine fnIndexIncrement variable=',
				variableName,
				'indexName=',
				indexName,
				'amount=',
				amount,
			);

		const varName = this.internalIndexVariableName(variableName, indexName);
		if (!varName) return false;

		// Default value is 1
		if (amount === undefined || amount === null) amount = 1;

		let currentValue = this.getVariable(varName);
		currentValue = DataTools.getNumber(currentValue);

		if (currentValue === undefined || currentValue === null || currentValue === false)
			currentValue = 0;

		if (currentValue === true) currentValue = 1;

		currentValue += amount;
		this.assign(varName, currentValue);
		return currentValue;
	}

	fnIndexGet(variableName: string, indexName?: string)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnIndexGet variable=', variableName, 'indexName=', indexName);

		const varName = this.internalIndexVariableName(variableName, indexName);
		if (varName) return this.getVariable(varName);

		return false;
	}

	/**
	 * Index helper function that returns the internal variable name.
	 * for example, index_name("counter", "day") returns "counter.days";
	 */
	fnIndexName(variableName: string, indexName?: string)
	{
		if (LogicEngine.debugOutput)
			console.log('LogicEngine fnIndexName variable=', variableName, 'indexName=', indexName);

		const varName = this.internalIndexVariableName(variableName, indexName);
		return varName;
	}

	/* Return a list of all variable names that match a given pattern */
	fnVariables(matchingText: string)
	{
		let resultList: string[] = [];
		const reMatch = new RegExp(matchingText, 'i');
		for (const key of Object.keys(this.values).sort())
		{
			if (typeof this.values[key] === 'function') continue;
			if (this.values[key] === undefined) continue;
			if (this.values[key] === null) continue;
			if (reMatch.test(key)) resultList.push(key);
		}

		return resultList;
	}

	/** Convert whatever is passed in to a logic command wrapped in  {{ and }} */
	fnCmdWrapper(source_value: any)
	{
		const strValue = DataTools.internalValidateString(source_value);
		return '{{ ' + strValue + ' }}';
	}

	/**
	Returns the query string filter for participant_id={{participant_uuid}}
	will use another ID if passed as a query
	*/
	fnFilterPid(strSomeId: string)
	{
		let someId = DataTools.internalValidateString(strSomeId).trim();
		if (someId === "") someId = this.getVariable("participant_uuid");
		return `participant_id=${someId}`;
	}

	/**
	 * Check to see if a value is empty and return it or the default value
	 * @param source_value
	 * @param default_value
	 * @returns
	 */
	fnCheck(source_value: any, default_value: any)
	{
		if (source_value === null) return default_value;
		if (source_value === undefined) return default_value;
		if (source_value === '') return default_value;
		if (typeof source_value === 'number' && isNaN(source_value)) return default_value;
		return source_value;
	}

	constructor()
	{
		if (LogicEngine.currentDeviceId === "unknownDevice")
		{
			// Set a default device_id
		}

		this.math = mathjs.create(mathjs.all, {});
		this.parser = (this.math as mathjs.MathJsStatic).parser();
		this.conditions = new ConditionEngine();

		let commands: any = {
			fpid: this.fnFilterPid.bind(this),
			suffix: this.fnSuffix.bind(this),
			now: this.fnNow.bind(this),
			current_year: this.fnCurrentYear.bind(this),
			current_user: this.fnCurrentUser.bind(this),
			current_participant: this.fnCurrentParticipant.bind(this),
			current_hour: this.fnCurrentHour.bind(this),
			lookup: this.fnLookup.bind(this),
			substr: this.fnSubstring.bind(this),
			substring: this.fnSubstring.bind(this),
			date_display: this.fnDisplayDate.bind(this),
			display_date: this.fnDisplayDate.bind(this),
			ellapsed_time: this.fnEllapseTime.bind(this),
			add_days: this.fnAddDays.bind(this),
			ms_to_years: this.fnMsToYears.bind(this),
			ms_to_days: this.fnMsToDays.bind(this),
			cat: this.fnConcatenate.bind(this),
			concatenate: this.fnConcatenate.bind(this),
			replace: this.fnReplace.bind(this),
			replace_all: this.fnReplaceAll.bind(this),
			route_match: this.fnRouteMatch.bind(this),
			not: this.fnNot.bind(this),
			equal: this.fnEqual.bind(this),
			lookup_equal: this.fnLookupEqual.bind(this),
			equals: this.fnEqual.bind(this),
			greater_than: this.fnGreaterThan.bind(this),
			greater_equal: this.fnGreaterEqual.bind(this),
			less_than: this.fnLessThan.bind(this),
			less_equal: this.fnLessEqual.bind(this),
			in_range: this.fnInRange.bind(this),
			all_true: this.fnAllTrue.bind(this),
			one_true: this.fnOneTrue.bind(this),
			some_true: this.fnSomeTrue.bind(this),
			time_ago: this.fnTimeAgo.bind(this),
			empty_or: this.fnEmptyOr.bind(this),
			empty: this.fnEmptyOr.bind(this),
			pretty_datetime: this.fnPrettyDateTime.bind(this),
			summary_text: this.fnSummaryText.bind(this),
			if: this.fnIf.bind(this),
			date_math: this.fnDateMath.bind(this),
			is_participant: this.fnIsParticipant.bind(this),
			timezone_offset: this.fnTimezoneOffset.bind(this),
			in_list: this.fnInList.bind(this),
			date_equals: this.fnDateEquals.bind(this),
			date_equal: this.fnDateEquals.bind(this),
			days_ago: this.fnDaysAgo.bind(this),
			contains: this.fnContains.bind(this),
			match: this.fnMatch.bind(this),
			length: this.fnLength.bind(this),
			index_reset: this.fnIndexReset.bind(this),
			index_set: this.fnIndexReset.bind(this),
			index_increment: this.fnIndexIncrement.bind(this),
			index_inc: this.fnIndexIncrement.bind(this),
			index_name: this.fnIndexName.bind(this),
			index_get: this.fnIndexGet.bind(this),
			get: this.fnIndexGet.bind(this),
			has_group: this.fnHasGroup.bind(this),
			get_groups: this.fnGetGroups.bind(this),
			variables: this.fnVariables.bind(this),
			cmd_wrap: this.fnCmdWrapper.bind(this),
			check: this.fnCheck.bind(this),
			route: this.fnRoute.bind(this),
			route_task: this.fnRoute.bind(this),
			encode: this.fnEncode.bind(this),
			decode: this.fnDecode.bind(this),
			get_json: this.fnJson.bind(this),
			is_future: this.fnIsFuture.bind(this),
			datefromid: this.fnDateFromObjectId.bind(this)
		};

		/* Register each function without _ in the name to make it easier for users */

		for (let name of Object.keys(commands))
		{
			let check = name.replace('_', '').replace('_', '');
			if (check !== name)
			{
				commands[check] = commands[name];
			}
		}

		this.import(commands);

		// Add additional forced values
		this.import({
			FALSE: false,
			TRUE: true,
			NULL: null,
			null: null,
			nil: null,
			is_silent: false,
			is_library: false,
			DateFormatSoon: "dddd, MMMM Do",
			DateFormatFull: "dddd, MMMM Do YYYY, h:mm:ss a",
			DateFormatYMD: "YYYY-MM-DD",
			DateFormatLocal: "LLL",
			DateFormatTimeLocal: "LTS",
			DateFormatDateLocal: "L",
			DateFormatTime: "YYYY-MM-DD hh:mm:ss"
		});

		// Load previously set global set values
		this.import(globalSetValues);
	}



	/**
	 * Generate Route to prevent cache in React or the browser.
	 * The parser can have a dummy random value inserted.
	 * It looks like this: /page/XXABCDEF/something.
	 * Here the value of “XX[A-Z]{6,9}” is just ignored.
	 *
	 * @param strPath Path to update
	 * @param args Additional parameters to append to the path
	 *
	 * @returns If pass '/page/XXABCDEF/test1', update the random value.
	 * If pass '/page/test1', insert random value in path.
	 * If pass '/test1', prepend '/page/XXABCDEF' to path.
	 */
	fnRoute(strPath: string, ...args: (string | number)[])
	{
		// Make sure path string
		strPath = DataTools.internalValidateString(strPath);

		// Remove random string or page from string
		strPath = strPath.replace(/^((\/?page)?(\/?XX[a-zA-Z]+)?\/)/, '');

		args = args
			.map((arg) =>
			{
				if (typeof arg === 'string')
				{
					arg = arg.replace(/^(\/)/, '');
				}

				return arg;
			})
			// Remove empty
			.filter((e) => !(typeof e === 'string' && !e));

		return `/page/XX${DataTools.generateRandomStr(8)}/${strPath}${args.length ? '/' + args.join('/') : ''
			}`;
	}

	/**
	 * Generate Task Route
	 * In some special cases a route may ask have a task associated with it.
	 * That route appears as /_task<id>/page/something.
	 *
	 * @param taskId
	 * @param strPath
	 * @param args
	 * @returns
	 */
	fnRouteTask(taskId: string, strPath: string, ...args: (string | number)[])
	{
		return `/${taskId}${this.fnRoute(strPath, ...args)}`;
	}
}
